import * as Api from '@/api';
import { defineModule, localActionContext } from 'direct-vuex';
import { WebRequestStatus } from '@/models';
import * as Utils from '@/utils';

interface MemberInfo {
  canDelete: boolean;
  status: WebRequestStatus;
}

const getInitialState = (): MemberInfo => {
  return {
    canDelete: false,
    status: WebRequestStatus.READY
  };
};

type MemberInfoMap = { [key: number]: MemberInfo };

const initialMap: MemberInfoMap = {};

function resetMemberInfo(mim: MemberInfoMap, memberId: number): MemberInfoMap {
  return {
    ...mim,
    [memberId]: getInitialState()
  };
}

function setMemberInfoStatus(
  mim: MemberInfoMap,
  memberId: number,
  updatedStatus: WebRequestStatus
): MemberInfoMap {
  const current = mim[memberId] ?? getInitialState();

  const updated = {
    ...current,
    status: updatedStatus
  };

  return {
    ...mim,
    [memberId]: updated
  };
}

function setMemberInfoCanDelete(
  mim: MemberInfoMap,
  memberId: number,
  updatedCanDelete: boolean
): MemberInfoMap {
  const current = mim[memberId] ?? getInitialState();

  const updated = {
    ...current,
    canDelete: updatedCanDelete
  };

  return {
    ...mim,
    [memberId]: updated
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const usageActionContext = (context: any) =>
  // eslint-disable-next-line  @typescript-eslint/no-use-before-define
  localActionContext(context, memberInfoStore);

const memberInfoStore = defineModule({
  namespaced: true as true,
  state: { memberInfo: initialMap },
  getters: {
    getMemberInfoById: state => (memberId: number) =>
      state.memberInfo[memberId] ?? getInitialState()
  },
  mutations: {
    reset(state, memberId: number) {
      console.log('resetting');
      state.memberInfo = resetMemberInfo(state.memberInfo, memberId);
    },

    setCanDelete(state, payload: { memberId: number; canDelete: boolean }) {
      console.log('setting canDelete');
      state.memberInfo = setMemberInfoCanDelete(
        state.memberInfo,
        payload.memberId,
        payload.canDelete
      );
    },

    setStatus(state, payload: { memberId: number; status: WebRequestStatus }) {
      console.log('setting status');
      state.memberInfo = setMemberInfoStatus(
        state.memberInfo,
        payload.memberId,
        payload.status
      );
    }
  },
  actions: {
    async getMemberInfo(
      context,
      payload: { organizationId: number; memberId: number }
    ) {
      const { commit } = usageActionContext(context);
      const { memberId, organizationId } = payload;

      commit.reset(memberId);
      commit.setStatus({ memberId, status: WebRequestStatus.READY });

      try {
        const canDelete = await Api.secure.canDeleteMember(
          organizationId,
          memberId
        );

        commit.setCanDelete({ memberId, canDelete });
        commit.setStatus({ memberId, status: WebRequestStatus.SUCCESS });

        await Utils.sleep(1000);
      } catch (error) {
        console.error(error);
        commit.setStatus({ memberId, status: WebRequestStatus.FAILURE });
        await Utils.sleep(4000);
      }
      commit.setStatus({ memberId, status: WebRequestStatus.READY });
    }
  }
});

export default memberInfoStore;
